import React from 'react';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';

/**
 * Alert dialog component (replacement for alert())
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {string} title - Dialog title (optional)
 * @param {string} message - Dialog message
 * @param {string} buttonText - Button text (default: "OK")
 * @param {function} onClose - Callback when user closes
 * @param {string} variant - Visual variant: "info" | "success" | "warning" | "error" (default: "info")
 */
export default function AlertDialog({
  isOpen,
  title,
  message,
  buttonText = 'OK',
  onClose,
  variant = 'info'
}) {
  if (!isOpen) return null;

  const variants = {
    info: {
      icon: Info,
      iconColor: 'text-indigo-600',
      bg: 'bg-indigo-100',
      border: 'border-indigo-200',
      button: 'bg-gradient-to-br from-indigo-600 to-indigo-700 hover:shadow-lg hover:shadow-indigo-500/30 border border-indigo-500/20'
    },
    success: {
      icon: CheckCircle,
      iconColor: 'text-emerald-600',
      bg: 'bg-emerald-100',
      border: 'border-emerald-200',
      button: 'bg-gradient-to-br from-emerald-600 to-emerald-700 hover:shadow-lg hover:shadow-emerald-500/30 border border-emerald-500/20'
    },
    warning: {
      icon: AlertCircle,
      iconColor: 'text-amber-600',
      bg: 'bg-amber-100',
      border: 'border-amber-200',
      button: 'bg-gradient-to-br from-amber-600 to-amber-700 hover:shadow-lg hover:shadow-amber-500/30 border border-amber-500/20'
    },
    error: {
      icon: AlertCircle,
      iconColor: 'text-red-600',
      bg: 'bg-red-100',
      border: 'border-red-200',
      button: 'bg-gradient-to-br from-red-600 to-red-700 hover:shadow-lg hover:shadow-red-500/30 border border-red-500/20'
    }
  };

  const style = variants[variant];
  const Icon = style.icon;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] max-w-md w-full p-6 md:p-8">
        {/* Close button */}
        <div className="flex justify-end mb-2">
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors duration-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Header - centered icon and title */}
        <div className="flex flex-col items-center mb-6">
          <div className={`w-14 h-14 ${style.bg} ${style.border} border rounded-full flex items-center justify-center mb-4 shadow-sm`}>
            <Icon className={`w-7 h-7 ${style.iconColor}`} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 text-center">
            {title || {
              info: 'Informacja',
              success: 'Sukces',
              warning: 'Uwaga',
              error: 'Błąd'
            }[variant]}
          </h3>
        </div>

        {/* Message */}
        <p className="text-slate-600 mb-6 whitespace-pre-line leading-relaxed text-center">{message}</p>

        {/* Action */}
        <button
          onClick={onClose}
          autoFocus
          className={`w-full px-6 py-3 text-white rounded-lg font-semibold transition-all duration-300 ${style.button}`}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}
